import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useActiveLeague } from "../hooks/useActiveLeague";

import { LeaguePageNav } from "../components/league/LeaguePageNav";
import { SeasonWrappedStory } from "../components/league/SeasonWrappedStory";
import { countViewerTrophies, pluralize } from "../utils/seasonAwards";
import { Spinner } from "../components/common/Spinner";
import { useGetLeagueBySlug } from "../api/leaguesApi.js";
import { useGetSeasonAwards } from "../api/seasonAwardsApi.js";
import { numToPrice } from "../components/utils";
import { shareOrCopy } from "../utils/share";

const TROPHY_PATH = "M7 2h10v2h4v3c0 2.6-1.9 4.7-4.4 5A6 6 0 0 1 13 15.7V18h3v2H8v-2h3v-2.3A6 6 0 0 1 7.4 12C4.9 11.7 3 9.6 3 7V4h4V2zm-2 4v1c0 1.3.8 2.4 2 2.8V6H5zm14 0h-2v3.8c1.2-.4 2-1.5 2-2.8V6z";

function TrophyIcon({ size = 46 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d={TROPHY_PATH} />
    </svg>
  );
}

function AwardTile({ kicker, name, team, detail }) {
  return (
    <div className="season-awards-tile">
      <div className="season-awards-tile-kicker">{kicker}</div>
      <div className="season-awards-tile-name">{name}</div>
      <div className="season-awards-tile-team">{team}</div>
      {detail && <div className="season-awards-tile-detail">{detail}</div>}
    </div>
  );
}

function SeasonAwardsPage() {
  const { slug } = useParams();
  const { activeLeague } = useActiveLeague();
  const { data: slugLeagueData, isLoading: isLoadingLeague } = useGetLeagueBySlug(slug);
  const rawLeague = slugLeagueData?.data;
  const league = rawLeague?.league ?? rawLeague;

  const { data: awardsData, isLoading: isLoadingAwards, error } = useGetSeasonAwards(league?._id);
  const [wrappedOpen, setWrappedOpen] = useState(false);

  const tablePath = `/${activeLeague?.slug ?? slug}/table`;

  const renderBody = () => {
    if (isLoadingLeague || isLoadingAwards || (!awardsData && !error)) {
      return (
        <div className="loader">
          <Spinner />
        </div>
      );
    }

    if (error || !awardsData) {
      return (
        <div className="season-awards-pending">
          <p>Could not load the season awards. Please try again later.</p>
          <Link to={tablePath} className="gold-cta gold-cta--ghost">Back to the table</Link>
        </div>
      );
    }

    const { season, isComplete, leagueName, totals, champion, awards, viewer } = awardsData;

    if (!isComplete) {
      return (
        <>
          <div className="title-block">
            <h1>Season {season}</h1>
          </div>
          <div className="season-awards-pending">
            <p>Season {season} is still running. Awards unlock when the final match is played.</p>
            <Link to={tablePath} className="gold-cta gold-cta--ghost">Back to the table</Link>
          </div>
        </>
      );
    }

    const trophies = countViewerTrophies(viewer);

    const handleShare = () => {
      let text = `Season ${season} of ${leagueName} is done`;
      text += champion ? `: ${champion.teamName} are champions.` : ".";
      if (viewer?.totwPicks > 0) {
        text += ` And my ${viewer.totwPicks} TOTW picks made the cut.`;
      }
      shareOrCopy({
        title: "Season Wrapped",
        text,
        url: `/${slug}/season-awards`,
      });
    };

    return (
      <>
        <div className="title-block">
          <h1>Season {season} Complete</h1>
        </div>
        <div className="season-awards-sub">
          {leagueName} | {pluralize(totals.matches, "match", "matches")} | {pluralize(totals.goals, "goal")}
        </div>
        <div className="season-awards-cta-row">
          <button type="button" className="gold-cta" onClick={() => setWrappedOpen(true)}>
            Play Season Wrapped
          </button>
        </div>

        {champion && (
          <div className="season-awards-champ">
            <span className="season-awards-champ-cup">
              <TrophyIcon size={46} />
            </span>
            <div className="season-awards-champ-body">
              <div className="season-awards-champ-kicker">Champions</div>
              <div className="season-awards-champ-team">{champion.teamName}</div>
              <div className="season-awards-champ-record">
                <b>W{champion.wins} D{champion.draws} L{champion.losses}</b> | {pluralize(champion.points, "point")}
              </div>
              {viewer?.isChampion && (
                <div className="season-awards-champ-you">That's you. Take the lap.</div>
              )}
            </div>
          </div>
        )}

        <div className="season-awards-grid">
          {awards?.goldenBoot && (
            <AwardTile
              kicker="Golden Boot"
              name={`${awards.goldenBoot.name}, ${awards.goldenBoot.goals} goals`}
              team={awards.goldenBoot.teamName}
            />
          )}
          {awards?.playmaker && (
            <AwardTile
              kicker="Playmaker"
              name={`${awards.playmaker.name}, ${awards.playmaker.assists} assists`}
              team={awards.playmaker.teamName}
            />
          )}
          {awards?.goldenGlove && (
            <AwardTile
              kicker="Golden Glove"
              name={`${awards.goldenGlove.name}, ${awards.goldenGlove.cleanSheets} clean sheets`}
              team={awards.goldenGlove.teamName}
            />
          )}
          {awards?.signingOfSeason && (
            <AwardTile
              kicker="Signing of the Season"
              name={`${awards.signingOfSeason.name}, ${numToPrice(awards.signingOfSeason.fee)}`}
              team={awards.signingOfSeason.teamName}
              detail={`${awards.signingOfSeason.goalsSinceJoining} goals since joining`}
            />
          )}
          {awards?.bestRating && (
            <AwardTile
              kicker="Best Average Rating"
              name={`${awards.bestRating.name}, ${Number(awards.bestRating.avgRating).toFixed(2)} avg`}
              team={awards.bestRating.teamName}
              detail={`over ${awards.bestRating.games} games`}
            />
          )}
        </div>

        <div className="season-awards-cabinet">
          <div className="season-awards-cabinet-head">
            <h3>Added to your cabinet</h3>
            <button type="button" className="gold-cta" onClick={handleShare}>
              Share Season Wrapped
            </button>
          </div>
          {trophies === 0 ? (
            <p className="season-awards-cabinet-empty">
              No trophies this season. Season {season + 1} is coming.
            </p>
          ) : (
            <div className="season-awards-shelf">
              {viewer?.isChampion && (
                <div className="season-awards-shelf-item">
                  <div className="season-awards-shelf-art season-awards-shelf-cup">
                    <TrophyIcon size={34} />
                  </div>
                  <div className="season-awards-shelf-title">League Champion</div>
                  <div className="season-awards-shelf-desc">{leagueName}, Season {season}</div>
                </div>
              )}
              {viewer?.totwPicks > 0 && (
                <div className="season-awards-shelf-item">
                  <div className="season-awards-shelf-art">
                    <span className="season-awards-hex">x{viewer.totwPicks}</span>
                  </div>
                  <div className="season-awards-shelf-title">TOTW Picks</div>
                  <div className="season-awards-shelf-desc">
                    {viewer.totwPicks} {viewer.totwPicks === 1 ? "pick" : "picks"} this season
                  </div>
                </div>
              )}
              {viewer?.hasGoldenGlove && (
                <div className="season-awards-shelf-item">
                  <div className="season-awards-shelf-art">
                    <span className="season-awards-plaque">
                      {awards?.goldenGlove ? `${awards.goldenGlove.cleanSheets} CS` : "CS"}
                    </span>
                  </div>
                  <div className="season-awards-shelf-title">Golden Glove</div>
                  <div className="season-awards-shelf-desc">
                    {awards?.goldenGlove
                      ? `${awards.goldenGlove.name}, ${awards.goldenGlove.cleanSheets} clean sheets`
                      : "Best defence in the league"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {wrappedOpen && (
          <SeasonWrappedStory data={awardsData} onClose={() => setWrappedOpen(false)} />
        )}
      </>
    );
  };

  return (
    <div className="page-league season-awards">
      <div className="wrapper">
        <LeaguePageNav />
        {renderBody()}
      </div>
    </div>
  );
}

export { SeasonAwardsPage };
